import { useState } from "react";
import { Link } from "react-router-dom";
import { forgotPassword } from "@/lib/api";
import { getErrorMessage } from "@/lib/utils";
import { EnvelopeSimple, ArrowLeft, CheckCircle } from "@phosphor-icons/react";
import BrandLogo from "@/components/BrandLogo";

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await forgotPassword(email.trim().toLowerCase());
      setSubmitted(true);
    } catch (err) {
      setError(getErrorMessage(err, "Something went wrong. Please try again."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="forgot-password-page">
      {/* Left - Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-white">
        <div className="w-full max-w-sm space-y-8">
          <BrandLogo
            iconClassName="w-10 h-10 rounded-lg"
            textClassName="text-2xl font-extrabold tracking-tight font-heading text-slate-900"
          />

          {submitted ? (
            /* Success state */
            <div className="space-y-6" data-testid="forgot-password-success">
              <div className="flex flex-col items-center text-center space-y-4 py-4">
                <div className="w-16 h-16 rounded-full bg-emerald-50 flex items-center justify-center">
                  <CheckCircle weight="duotone" className="w-9 h-9 text-emerald-500" />
                </div>
                <div>
                  <h1 className="text-2xl font-black tracking-tight font-heading text-slate-900">
                    Check your email
                  </h1>
                  <p className="text-sm text-slate-500 mt-2 leading-relaxed">
                    If <span className="font-semibold text-slate-700">{email}</span> is registered,
                    we've sent a password reset link. It expires in 1 hour.
                  </p>
                </div>
              </div>
              <div className="bg-slate-50 border border-slate-200 rounded-md px-4 py-3 text-xs text-slate-500 leading-relaxed">
                Didn't receive it? Check your spam folder, or{" "}
                <button
                  className="text-foreground font-bold hover:underline"
                  onClick={() => { setSubmitted(false); setEmail(""); }}
                >
                  try a different email
                </button>.
              </div>
              <Link
                to="/login"
                className="flex items-center justify-center gap-2 w-full h-11 border border-slate-200 rounded-md text-sm font-bold text-slate-700 hover:bg-slate-50 transition-colors"
              >
                <ArrowLeft weight="bold" className="w-4 h-4" />
                Back to Sign In
              </Link>
            </div>
          ) : (
            /* Request form */
            <div className="space-y-6">
              <div>
                <h1 className="text-3xl font-black tracking-tight font-heading text-slate-900">
                  Forgot password?
                </h1>
                <p className="text-sm text-slate-500 mt-1 font-medium leading-relaxed">
                  Enter your email and we'll send you a link to reset your password.
                </p>
              </div>

              {error && (
                <div data-testid="forgot-password-error" className="bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 rounded-md font-medium">
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5 block">
                    Email address
                  </label>
                  <div className="relative">
                    <EnvelopeSimple weight="bold" className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                    <input
                      data-testid="forgot-password-email"
                      type="email"
                      autoComplete="email"
                      required
                      className="w-full h-12 pl-10 pr-4 text-sm border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black/10 focus:border-black font-medium"
                      placeholder="you@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                    />
                  </div>
                </div>

                <button
                  data-testid="forgot-password-submit"
                  type="submit"
                  disabled={loading || !email.trim()}
                  className="w-full h-12 bg-primary text-primary-foreground rounded-md text-sm font-bold hover:bg-primary/90 disabled:opacity-50 transition-colors"
                >
                  {loading ? "Sending..." : "Send Reset Link"}
                </button>
              </form>

              <Link
                to="/login"
                className="flex items-center justify-center gap-2 text-sm text-slate-500 font-medium hover:text-slate-700 transition-colors"
              >
                <ArrowLeft weight="bold" className="w-3.5 h-3.5" />
                Back to Sign In
              </Link>
            </div>
          )}
        </div>
      </div>

      {/* Right - Hero */}
      <div className="hidden lg:flex flex-1 bg-slate-900 items-center justify-center p-12 relative overflow-hidden">
        <div className="relative z-10 text-center space-y-6 max-w-md">
          <EnvelopeSimple weight="duotone" className="w-24 h-24 text-foreground mx-auto" />
          <h2 className="text-3xl font-black tracking-tight font-heading text-white">
            Secure Password Reset
          </h2>
          <p className="text-slate-400 text-base font-medium leading-relaxed">
            We'll email you a secure, one-time link to reset your password. It's valid for 1 hour.
          </p>
        </div>
        <div className="absolute inset-0 bg-gradient-to-br from-black/10 to-transparent" />
      </div>
    </div>
  );
}
